import {useCallback, useContext, useEffect} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {DialogActions, Typography} from '@mui/material';

import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import EmpionDialog from '../../../../../../components/EmpionDialog/EmpionDialog';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../../../contexts/UserContext/UserContext';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
import {Post} from '../../../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../../../lib/api/POST/PostRoutes';
import {AppConfig} from '../../../../../../lib/AppConfig';
import {logout as authServiceLogout} from '../../../../../../services/auth-service';

interface Props {
  onClose: () => void;
  isOpen: boolean;
}
export const DeleteAccountAlert = ({onClose, isOpen}: Props) => {
  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation();

  const navigate = useNavigate();
  const {userContextLogout} = useContext(UserContext);
  const onSuccess = useCallback(() => {
    authServiceLogout();
    userContextLogout();
    navigate(AppConfig.LOGIN_PATH);
    localStorage.clear();
  }, [userContextLogout, navigate]);

  const {makeRequest, error} = useApiWrite<Post.DeleteAccount>({
    method: 'post',
    path: PostRoutes[Post.DeleteAccount],
    onSuccess,
  });

  useEffect(() => {
    if (error) {
      dispatchMessage(error.extra_details, 'error');
    }
  }, [error, dispatchMessage]);

  const backgroundColor = orangeColors[5];

  const deleteAccount = () => {
    makeRequest(undefined);
  };

  return (
    <EmpionDialog open={isOpen} onClose={onClose} backgroundColor={backgroundColor}>
      <Typography variant='h3' paddingBottom='16px'>
        {t('settings.accountManagement.deleteAccount.title')}
      </Typography>
      <Typography data-testid='delete-acct-modal' variant='body1'>
        {t('settings.accountManagement.deleteAccount.modalText')}
      </Typography>
      <DialogActions>
        <EmpionButton onClick={deleteAccount}>
          {t('settings.accountManagement.deleteAccount.confirm')}
        </EmpionButton>
        <EmpionButton
          color='primary'
          onClick={onClose}
          disabledColor={greenColors['40']}
          sx={{backgroundColor: greenColors[900]}}
          autoFocus
        >
          {t('settings.accountManagement.deleteAccount.cancel')}
        </EmpionButton>
      </DialogActions>
    </EmpionDialog>
  );
};
